import { FieldErrors, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box, Tooltip } from '@mui/material';
import { Typography } from '@mui/material';
import { Button } from '@mui/material';
import { CircularProgress } from '@mui/material';
import { UseFormReturnType } from '@refinedev/react-hook-form';
import { BaseRecord } from '@refinedev/core';
import { HttpError } from '@refinedev/core';
import { flattenErrors } from '@/shared/utils/errors';

// Validation Error Tooltip Component
const ValidationErrorTooltip = ({
  errors,
  children,
}: {
  errors: FieldErrors<Record<string, unknown>>;
  children: React.ReactElement;
}) => {
  const { t } = useTranslation();
  const hasErrors = errors && Object.keys(errors).length > 0;

  if (!hasErrors) {
    return children;
  }

  const errorMessages = flattenErrors(errors);

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            {t('forms.errors.validation')}
          </Typography>
          <Typography
            variant="body1"
            component="pre"
            sx={{ whiteSpace: 'pre-line' }}
          >
            {errorMessages.map((error, index) => (
              <Typography variant="body2" key={index}>
                {' '}
                • {error}
              </Typography>
            ))}
          </Typography>
        </Box>
      }
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            maxWidth: 300,
            fontSize: '0.875rem',
          },
        },
      }}
    >
      {children}
    </Tooltip>
  );
};

export const SubmitButton = <
  TQuery extends BaseRecord,
  TVariable extends FieldValues,
>({
  form,
}: {
  form: UseFormReturnType<TQuery, HttpError, TVariable>;
}) => {
  const { t } = useTranslation();
  const { saveButtonProps, formState } = form;
  const { errors, isSubmitting } = formState;
  const hasErrors = errors && Object.keys(errors).length > 0;
  return (
    <ValidationErrorTooltip errors={formState.errors}>
      <span>
        <Button
          {...saveButtonProps}
          variant="contained"
          disabled={hasErrors || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
        >
          {t('buttons.save')}
        </Button>
      </span>
    </ValidationErrorTooltip>
  );
};
